import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';
import { useToast } from '../../components/Toast/ToastContext';
import { useRescueContext } from '../../lib/RescueContext';
import { trackGwaBlurbCopied, trackGwaCodeCopied, trackGwaDashboardViewed, trackGwaLinkCopied } from '../../analytics';
import { rescueGwaPostcardsQuery, RescueGwaPostcardsData } from '../../lib/rescueQueries';
import Button from '../../components/Button/Button';
import type { GwaRedemptionItem } from './Gwa';
import GwaPostcardsSection from './GwaPostcardsSection';
import OrderPostcardsModal from './OrderPostcardsModal';
import { useAdoptionCardPreviews } from './useAdoptionCardPreviews';
import styles from './GwaDashboard.module.scss';

interface Enrollment {
  id: string;
  slug: string;
  promoCodeAlias: string;
  pdpUrl: string;
  active: boolean;
  createdAt: string;
  totalRedemptions: number;
  monthRedemptions: number;
  recentRedemptions: GwaRedemptionItem[];
}

interface GwaDashboardProps {
  enrollment: Enrollment;
}

function buildBlurb(template: string, pdpUrl: string, promoCode: string, orgName: string) {
  return template
    .replace(/\{\{pdpUrl\}\}/g, pdpUrl)
    .replace(/\{\{promoCode\}\}/g, promoCode)
    .replace(/\{\{orgName\}\}/g, orgName);
}

const EMAIL_TEMPLATE = `Congrats on your new family member! {{orgName}} has partnered with Fi to give you a free Fi Mini GPS Tracker — the smallest, lightest dog GPS ever made.

The Fi Mini weighs just half an ounce, lasts up to 6 weeks on a single charge, and tracks your dog's location in real time. Plus you'll get daily activity and sleep insights to learn your new dog's routine.

Visit {{pdpUrl}} and use code {{promoCode}} at checkout to claim yours — 6 months free, then $189/yr.

A one-time $20 activation fee applies.`;

const PRINT_TEMPLATE = `FREE FI MINI GPS TRACKER FOR YOUR NEW DOG

{{orgName}} has partnered with Fi to give you a free Fi Mini — the smallest, lightest dog GPS ever made.

• Real-time GPS location tracking
• Daily activity & sleep insights
• Half an ounce with 6-week battery life

Claim yours at: {{pdpUrl}}
Use code: {{promoCode}}
6 months free, then $189/yr.
A one-time $20 activation fee applies.`;

type BlurbTab = 'email' | 'print';

const CARDS_SHIPPED_STATUSES = new Set(['shipped', 'delivered']);

export default function GwaDashboard({ enrollment }: GwaDashboardProps) {
  const { showToast } = useToast();
  const { orgName } = useRescueContext();
  const history = useHistory();
  const [activeBlurbTab, setActiveBlurbTab] = useState<BlurbTab>('email');
  const [orderModalOpen, setOrderModalOpen] = useState(false);

  const { data: postcardsData } = useQuery<RescueGwaPostcardsData>(rescueGwaPostcardsQuery);
  const orders = postcardsData?.rescueGwaPostcardOrders ?? [];
  const cardsShipped = orders
    .filter((order) => CARDS_SHIPPED_STATUSES.has(order.status))
    .reduce((sum, order) => sum + order.quantity, 0);

  // Ordering is closed-beta; stay hidden until the backend confirms eligibility rather than
  // flashing a button that would fail on submit. Past orders keep showing either way, so a
  // rescue dropped from the beta can still track what it already ordered.
  const canOrderCards = postcardsData?.rescueGwaPostcardsEligible ?? false;
  const showCards = canOrderCards || orders.length > 0;

  const previews = useAdoptionCardPreviews(showCards);

  // biome-ignore lint/correctness/useExhaustiveDependencies: fire once per mount, not on stats refetch
  useEffect(() => {
    trackGwaDashboardViewed(enrollment.totalRedemptions, enrollment.monthRedemptions);
  }, []);

  const emailBlurb = buildBlurb(EMAIL_TEMPLATE, enrollment.pdpUrl, enrollment.promoCodeAlias, orgName);
  const printBlurb = buildBlurb(PRINT_TEMPLATE, enrollment.pdpUrl, enrollment.promoCodeAlias, orgName);
  const landingPageUrl = /^https?:\/\//.test(enrollment.pdpUrl) ? enrollment.pdpUrl : `https://${enrollment.pdpUrl}`;

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(
      () => showToast(`${label} copied!`),
      () => showToast('Failed to copy'),
    );
  };

  return (
    <div>
      <div className={styles.pageHead}>
        <h1>Gift with Adoption</h1>
        {canOrderCards && (
          <Button variant="green" onClick={() => setOrderModalOpen(true)}>
            Get Adoption Cards
          </Button>
        )}
      </div>

      <div className={styles.successBanner}>
        <svg viewBox="0 0 24 24" className={styles.checkIcon}>
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <span>You're enrolled. Your adopter landing page and promo code are live.</span>
      </div>

      <div className={`${styles.statsGrid} ${showCards ? '' : styles.statsGridTwoUp}`}>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Redemptions</div>
          <div className={styles.statValue}>{enrollment.monthRedemptions}</div>
          <div className={styles.statSubtitle}>last 30 days</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Total Redemptions</div>
          <div className={styles.statValue}>{enrollment.totalRedemptions}</div>
          <div className={styles.statSubtitle}>all time</div>
        </div>
        {showCards && (
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Cards in Adoption Packets</div>
            <div className={styles.statValue}>{cardsShipped}</div>
            <div className={styles.statSubtitle}>
              {cardsShipped === 0 ? 'order below to get started' : 'shipped to your rescue'}
            </div>
          </div>
        )}
      </div>

      <div className={styles.offerCard}>
        <div className={styles.offerHeader}>
          <div className={styles.offerTitle}>Your adopter landing page</div>
          <Button variant="ghost" size="sm" onClick={() => window.open(landingPageUrl, '_blank', 'noopener')}>
            View page
          </Button>
        </div>
        <p className={styles.offerDescription}>
          Your rescue has a custom page where every adopter can claim a{' '}
          <strong>Fi Mini GPS tracker — 6 months free</strong> (renews at $189/yr). Anyone who visits your link and
          applies your code gets the offer. Share it with recent and previous adopters — not for public or social media
          use.
          <span className={styles.feeNote}>A one-time $20 activation fee applies.</span>
        </p>
        <div className={styles.offerFields}>
          <div className={styles.offerField}>
            <div className={styles.offerFieldLabel}>Your Link</div>
            <div className={styles.offerFieldValue}>
              <span>{enrollment.pdpUrl}</span>
              <button
                type="button"
                className={styles.copyBtn}
                onClick={() => {
                  copyToClipboard(enrollment.pdpUrl, 'Link');
                  trackGwaLinkCopied();
                }}
              >
                Copy
              </button>
            </div>
          </div>
          <div className={styles.offerField}>
            <div className={styles.offerFieldLabel}>Promo Code</div>
            <div className={styles.offerFieldValue}>
              <span className={styles.mono}>{enrollment.promoCodeAlias}</span>
              <button
                type="button"
                className={styles.copyBtn}
                onClick={() => {
                  copyToClipboard(enrollment.promoCodeAlias, 'Promo code');
                  trackGwaCodeCopied();
                }}
              >
                Copy
              </button>
            </div>
          </div>
        </div>
      </div>

      {showCards && (
        <GwaPostcardsSection
          orders={orders}
          previews={previews}
          canOrderCards={canOrderCards}
          onOrderCards={() => setOrderModalOpen(true)}
        />
      )}

      <div className={styles.blurbCard}>
        <div className={styles.blurbHeader}>
          <div className={styles.offerTitle}>Ready-to-send message</div>
          <div className={styles.blurbTabs}>
            <button
              type="button"
              className={`${styles.blurbTab} ${activeBlurbTab === 'email' ? styles.blurbTabActive : ''}`}
              onClick={() => setActiveBlurbTab('email')}
            >
              Email
            </button>
            <button
              type="button"
              className={`${styles.blurbTab} ${activeBlurbTab === 'print' ? styles.blurbTabActive : ''}`}
              onClick={() => setActiveBlurbTab('print')}
            >
              Print
            </button>
          </div>
        </div>
        <p className={styles.blurbIntro}>
          Copy this to send to adopters by email, or print it for your packets. Your link and code are filled in.
        </p>
        <pre className={styles.blurbText}>{activeBlurbTab === 'email' ? emailBlurb : printBlurb}</pre>
        <button
          type="button"
          className={styles.copyBtn}
          onClick={() => {
            copyToClipboard(activeBlurbTab === 'email' ? emailBlurb : printBlurb, 'Blurb');
            trackGwaBlurbCopied(activeBlurbTab);
          }}
        >
          Copy to clipboard
        </button>
      </div>

      <div className={styles.crossSellCard}>
        <div className={styles.crossSellBody}>
          <div className={styles.crossSellBadge}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 21s-7-5.2-7-11a7 7 0 0 1 14 0c0 5.8-7 11-7 11z" />
              <circle cx="12" cy="10" r="2.5" />
            </svg>
            Protect every dog you microchip
          </div>
          <div className={styles.offerTitle}>Microchips aren't enough</div>
          <p className={styles.offerDescription}>
            A microchip only reunites a dog after someone finds and scans it. With real-time GPS, adopters can locate a
            runaway dog the moment it gets out — not just hope they're found by a friendly stranger. Fi markets a GPS
            device to every dog you microchip, so owners can protect them proactively.
          </p>
          <p className={styles.offerDescription}>
            Connect your microchip registry to reach those owners — add an integration or upload a CSV.
          </p>
          <div className={styles.crossSellAutoPill}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Automatically on when you chip with Fi Nano microchips
          </div>
          <div className={styles.crossSellActions}>
            <Button variant="green" onClick={() => history.push('/rescue/integrations')}>
              Manage integrations
            </Button>
            <Button variant="ghost" onClick={() => history.push('/rescue/integrations')}>
              Upload CSV
            </Button>
          </div>
        </div>
        <div className={styles.crossSellVisual}>
          <div className={styles.flowRow}>
            <span className={styles.flowIcon}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 21s-7-5.2-7-11a7 7 0 0 1 14 0c0 5.8-7 11-7 11z" />
                <circle cx="12" cy="10" r="2.5" />
              </svg>
            </span>
            You microchip a dog
          </div>
          <div className={styles.flowRow}>
            <span className={styles.flowIcon}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 6h16v12H4z" />
                <path d="M4 7l8 6 8-6" />
              </svg>
            </span>
            Fi reaches the owner
          </div>
          <div className={styles.flowRow}>
            <span className={styles.flowIcon}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="8" />
                <circle cx="12" cy="12" r="2.5" />
              </svg>
            </span>
            Owner adds real-time GPS
          </div>
        </div>
      </div>

      <div className={styles.activationsCard}>
        <div className={styles.offerTitle}>Adopter Activations</div>
        {enrollment.recentRedemptions.length === 0 ? (
          <p className={styles.emptyTable}>
            No activations yet. Share your link and promo code with adopters to get started.
          </p>
        ) : (
          <table className={styles.activationsTable}>
            <thead>
              <tr>
                <th>Pet Name</th>
                <th>Adopter</th>
                <th>Breed</th>
                <th>Age</th>
                <th>Activated</th>
              </tr>
            </thead>
            <tbody>
              {enrollment.recentRedemptions.map((r) => (
                <tr key={r.id}>
                  <td>{r.petName ?? '—'}</td>
                  <td>{r.adopterName}</td>
                  <td>{r.breed ?? '—'}</td>
                  <td>{r.age ?? '—'}</td>
                  <td>{DateTime.fromISO(r.redeemedAt).toLocaleString(DateTime.DATE_MED)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {canOrderCards && (
        <OrderPostcardsModal
          open={orderModalOpen}
          onClose={() => setOrderModalOpen(false)}
          promoCode={enrollment.promoCodeAlias}
          frontImage={previews.front}
        />
      )}
    </div>
  );
}
